import { Controller } from "@hotwired/stimulus"

// The left-hand navigation.
//
// Three independent things, deliberately kept apart because they answer
// different questions:
//
//   toggle()        — hide the sidebar altogether (the hamburger). This is the
//                     "give me the whole screen" case, and the mobile case.
//   toggleRail()    — narrow it to an icon rail. Still navigable: hovering a
//                     group opens its items beside the rail.
//   toggleSection() — open or close ONE group.
//
// All three remember what you chose, per browser. Groups default to CLOSED,
// which keeps a hundred-item menu readable; the group holding the current page
// is opened by the server and highlighted, so the nav always says which module
// you are in — including once you close it again.
const HIDDEN_KEY = "bizsuite:sidebar"
const RAIL_KEY = "bizsuite:sidebar:rail"
const OPEN_SECTIONS_KEY = "bizsuite:sidebar:open"

export default class extends Controller {
  static targets = ["panel", "content", "section", "railIcon"]

  connect() {
    const stored = this.read(HIDDEN_KEY)
    const hidden = stored === null ? window.innerWidth < 1366 : stored === "collapsed"
    this.setHidden(hidden)
    this.setRail(this.read(RAIL_KEY) === "rail")
    this.restoreSections()
  }

  // ------------------------------------------------------------ hide / show

  toggle() {
    this.setHidden(!this.hidden)
    this.write(HIDDEN_KEY, this.hidden ? "collapsed" : "open")
  }

  setHidden(hidden) {
    this.hidden = hidden
    if (!this.hasPanelTarget || !this.hasContentTarget) return

    this.panelTarget.classList.toggle("-translate-x-full", hidden)
    this.applyContentOffset()
  }

  // ------------------------------------------------------------- rail mode

  toggleRail() {
    this.setRail(!this.rail)
    this.write(RAIL_KEY, this.rail ? "rail" : "full")
  }

  setRail(rail) {
    this.rail = rail
    if (!this.hasPanelTarget) return

    this.panelTarget.classList.toggle("sidebar-rail", rail)
    this.panelTarget.classList.toggle("w-64", !rail)
    if (this.hasRailIconTarget) {
      // The chevron points the way the click will take you.
      this.railIconTarget.classList.toggle("rotate-180", rail)
    }
    this.applyContentOffset()
  }

  // The page sits beside the sidebar, so its left padding has to follow
  // whichever state the sidebar is in. Kept in one place so the three toggles
  // cannot disagree about it.
  applyContentOffset() {
    if (!this.hasContentTarget) return

    const classes = this.contentTarget.classList
    classes.toggle("lg:pl-64", !this.hidden && !this.rail)
    classes.toggle("lg:pl-[4.25rem]", !this.hidden && this.rail)
    classes.toggle("pl-0", this.hidden)
  }

  // --------------------------------------------------------------- groups

  toggleSection(event) {
    const section = event.currentTarget.closest(".nav-section")
    if (!section) return

    section.dataset.clicked = "true"
    this.applySection(section, section.dataset.open !== "true")
    this.rememberSections()
  }

  applySection(section, open) {
    section.dataset.open = String(open)
    const button = section.querySelector(".nav-section-toggle")
    if (button) button.setAttribute("aria-expanded", String(open))
  }

  // Re-opens the groups this reader had left open. The one holding the current
  // page is already open from the server and is left alone.
  restoreSections() {
    const open = new Set(this.readJSON(OPEN_SECTIONS_KEY))

    this.sectionTargets.forEach((section) => {
      if (section.dataset.holdsCurrentPage === "true") return
      if (!open.has(section.dataset.sectionKey)) return

      this.applySection(section, true)
    })
  }

  // The current page's group is opened by the server, so merely visiting a page
  // must not record it as a group the reader chose to open. Its stored state is
  // carried through untouched unless they actually clicked it.
  rememberSections() {
    const stored = new Set(this.readJSON(OPEN_SECTIONS_KEY))

    const open = this.sectionTargets
      .filter((section) => {
        const key = section.dataset.sectionKey
        if (section.dataset.holdsCurrentPage === "true" && !section.dataset.clicked) {
          return stored.has(key)
        }
        return section.dataset.open === "true"
      })
      .map((section) => section.dataset.sectionKey)

    this.write(OPEN_SECTIONS_KEY, JSON.stringify(open))
  }

  // ---------------------------------------------------------------- storage
  // Private windows and blocked site data make localStorage throw rather than
  // return null, so every read and write is guarded and the nav simply falls
  // back to its defaults.

  read(key) {
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  }

  readJSON(key) {
    try {
      return JSON.parse(this.read(key)) || []
    } catch {
      return []
    }
  }

  write(key, value) {
    try {
      localStorage.setItem(key, value)
    } catch {
      /* nothing to do — the preference just will not persist */
    }
  }
}
